import { nextTick, reactive, ref } from 'vue'
import type { Ref, ShallowRef } from 'vue'
import OrionError from '../../../error/OrionError'
import type { EdittableInstance } from 'orion-design'
import { uuid } from '../../../utils'
import { isEqual, extend, isFunction } from 'lodash-es'
import { createRowData } from '../utils'

interface UseEdittableOptions<Row> {
  tableRef?: Readonly<ShallowRef<EdittableInstance | null>>
  defaultData?: Row[]
  newRowTemplate?: Record<string, any> | (() => Record<string, any>)
  afterSelectionChange?: (newSelection: Row[]) => void
  afterCurrentRowChange?: (currentRow: Row, oldCurrentRow: Row) => void
}

const useEdittable = <Row = any>(options: UseEdittableOptions<Row>) => {
  const { tableRef, defaultData, newRowTemplate, afterSelectionChange, afterCurrentRowChange } = options

  const data = ref<Row[]>(defaultData ? defaultData : []) as Ref<Row[]>
  const selection = ref<Row[]>([]) as Ref<Row[]>
  const currentRow = ref<Row>()

  const onSelectionChange = (newSelection: Row[]) => {
    selection.value = newSelection
    afterSelectionChange && afterSelectionChange(newSelection)
  }
  const onCurrentRowChange = (newCurrentRow: Row, oldCurrentRow: Row) => {
    currentRow.value = newCurrentRow
    afterCurrentRowChange && afterCurrentRowChange(newCurrentRow, oldCurrentRow)
  }

  const clearSelection = () => {
    if (!tableRef) {
      throw new OrionError(`调用clearSelection失败,tableRef为空`)
    }
    tableRef.value!.clearSelection()
  }
  const toggleRowSelection = (row: Row, selected?: boolean) => {
    if (!tableRef) {
      throw new OrionError(`调用toggleRowSelection失败,tableRef为空`)
    }
    tableRef.value!.toggleRowSelection(row, selected)
  }
  const toggleAllSelection = () => {
    if (!tableRef) {
      throw new OrionError(`调用toggleAllSelection失败,tableRef为空`)
    }
    tableRef.value!.toggleAllSelection()
  }
  const getSelectionRows = () => {
    if (!tableRef) {
      throw new OrionError(`调用getSelectionRows失败,tableRef为空`)
    }
    return tableRef.value!.getSelectionRows() as Row[]
  }
  const setCurrentRow = (row: Row) => {
    if (!tableRef) {
      throw new OrionError(`调用setCurrentRow失败,tableRef为空`)
    }
    tableRef.value!.setCurrentRow(row)
  }
  const scrollToRow = (index: number) => {
    if (!tableRef) {
      throw new OrionError(`调用scrollToRow失败,tableRef为空`)
    }
    tableRef.value!.scrollToRow(index)
  }

  const appendRow = (rowData: any) => {
    data.value.push(createRowData(newRowTemplate, rowData))
  }
  const insertRow = (rowData: any, index: number) => {
    data.value.splice(index, 0, createRowData(newRowTemplate, rowData))
  }
  const deleteRow = (index: number) => {
    data.value.splice(index, 1)
  }
  const updateRow = (rowData: any, index: number) => {
    data.value[index] = createRowData(newRowTemplate, rowData)
  }
  const moveUp = (index: number) => {
    moveTo(index, index - 1)
  }
  const moveDown = (index: number) => {
    moveTo(index, index + 1)
  }
  const moveTo = (from: number, to: number) => {
    const item = data.value.splice(from, 1)
    data.value.splice(to, 0, item[0])
  }
  const selectionGroup = () => {
    if (selection.value.length < 2) {
      return
    }

    let firstRowIndex: number = 0
    let firstRowFlag: boolean = true
    const leftRowIndexs: number[] = []

    data.value.forEach((e, i) => {
      selection.value.forEach((item) => {
        if (isEqual(e, item)) {
          if (firstRowFlag) {
            firstRowIndex = i
            firstRowFlag = false
          } else {
            leftRowIndexs.push(i)
          }
          return false
        }
      })
    })

    leftRowIndexs.forEach((e, i) => {
      moveTo(e, firstRowIndex + i + 1)
    })
  }

  return reactive({
    data,
    selection,
    currentRow,
    onSelectionChange,
    onCurrentRowChange,
    clearSelection,
    toggleRowSelection,
    toggleAllSelection,
    getSelectionRows,
    setCurrentRow,
    scrollToRow,
    appendRow,
    insertRow,
    deleteRow,
    updateRow,
    moveUp,
    moveDown,
    moveTo,
    selectionGroup,
  })
}

export default useEdittable
